'use client'

import * as React from 'react'
import { CheckIcon } from 'lucide-react'

import { cn } from '../../lib/utils'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalPortal,
  ModalTitle,
} from '../modal'
import { inputContainerVariants } from '../input/input-container'
import { Icon } from '../icon'
import { Input } from '../input'
import { Button } from '../button'
import type {
  SelectModalContentProps,
  SelectModalGroupProps,
  SelectModalItemProps,
  SelectModalItemsProps,
  SelectModalLabelProps,
  SelectModalProps,
  SelectModalSeparatorProps,
  SelectModalTriggerProps,
  SelectModalValueProps,
} from './select-modal.types'

const SelectModalContext = React.createContext<{
  open: boolean
  onOpenChange: (open: boolean) => void
  value?: string
  onValueSelect: (value: string) => void
  selectedValue?: string
  searchTerm: string
  setSearchTerm: (term: string) => void
} | null>(null)

function useSelectModalContext() {
  const context = React.useContext(SelectModalContext)
  if (!context) {
    throw new Error('SelectModal components must be used within SelectModal')
  }
  return context
}

export function useSelectModalSearch() {
  const context = useSelectModalContext()
  return {
    searchTerm: context.searchTerm,
    setSearchTerm: context.setSearchTerm,
  }
}

function SelectModal({
  children,
  value,
  onValueChange,
  defaultValue,
  disabled,
}: SelectModalProps) {
  const [open, setOpen] = React.useState(false)
  const [internalValue, setInternalValue] = React.useState<string | undefined>(
    defaultValue,
  )
  const [searchTerm, setSearchTerm] = React.useState('')
  const selectedValue = value ?? internalValue

  const handleValueSelect = React.useCallback(
    (newValue: string) => {
      if (!value) {
        setInternalValue(newValue)
      }
      onValueChange?.(newValue)
      setOpen(false)
      setSearchTerm('')
    },
    [value, onValueChange],
  )

  React.useEffect(() => {
    if (!open) {
      setSearchTerm('')
    }
  }, [open])

  return (
    <SelectModalContext.Provider
      value={{
        open,
        onOpenChange: setOpen,
        value: selectedValue,
        onValueSelect: handleValueSelect,
        selectedValue,
        searchTerm,
        setSearchTerm,
      }}
    >
      <div
        data-slot='select-modal'
        className={disabled ? 'opacity-50 pointer-events-none' : ''}
      >
        {children}
      </div>
    </SelectModalContext.Provider>
  )
}

function SelectModalValue({
  placeholder,
  children,
  customValue: CustomValue,
}: SelectModalValueProps) {
  const { selectedValue } = useSelectModalContext()

  if (children) {
    return <>{children}</>
  }

  if (CustomValue) {
    return <CustomValue value={selectedValue} placeholder={placeholder} />
  }

  return (
    <span
      data-slot='select-modal-value'
      className={cn(!selectedValue && 'text-neutral-surface-60')}
    >
      {selectedValue || placeholder}
    </span>
  )
}

function SelectModalTrigger({
  className,
  inputSize = 'sm',
  variant = 'fill',
  children,
  asChild,
  ...props
}: SelectModalTriggerProps) {
  const { open, onOpenChange } = useSelectModalContext()

  const handleClick = () => {
    onOpenChange(!open)
  }

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      onClick: handleClick,
      ...props,
    } as React.HTMLAttributes<HTMLElement>)
  }

  return (
    <button
      type='button'
      data-slot='select-modal-trigger'
      data-size={inputSize}
      onClick={handleClick}
      className={cn(
        'items-center justify-between whitespace-nowrap',
        inputContainerVariants({
          inputSize,
          variant,
        }),
        className,
      )}
      {...props}
    >
      {children}
      <Icon name='TriangleDown' className='size-icon-sm' />
    </button>
  )
}

function SelectModalContent({
  title,
  className,
  children,
  modalClassName,
  showSearch = true,
  searchPlaceholder = 'search...',
  backButtonLabel = 'back',
}: SelectModalContentProps) {
  const { open, onOpenChange, searchTerm, setSearchTerm } =
    useSelectModalContext()

  if (!open) return null

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalPortal>
        <ModalOverlay />
        <ModalContent
          className={cn('sm:max-w-[525px]', modalClassName)}
          data-slot='select-modal-content'
        >
          <ModalHeader>
            <ModalTitle className='text-neutral-surface-100'>
              {title}
            </ModalTitle>
          </ModalHeader>
          {showSearch && (
            <div className=' pb-md'>
              <div className='relative'>
                <Icon
                  name='SearchLine'
                  className='absolute start-2 top-1/2 -translate-y-1/2 size-icon-sm text-neutral-surface-60 pointer-events-none'
                />
                <Input
                  type='text'
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className='ps-8'
                  inputSize='md'
                />
              </div>
            </div>
          )}
          <div className={cn('py-1 max-h-[60vh] overflow-y-auto', className)}>
            {children}
          </div>

          <Button
            variant='ghost'
            color='neutral'
            size='md'
            width='full'
            onClick={() => onOpenChange(false)}
          >
            {backButtonLabel}
          </Button>
        </ModalContent>
      </ModalPortal>
    </Modal>
  )
}

function SelectModalGroup({ children, className }: SelectModalGroupProps) {
  return (
    <div data-slot='select-modal-group' className={className}>
      {children}
    </div>
  )
}

function SelectModalLabel({ className, children }: SelectModalLabelProps) {
  return (
    <div
      data-slot='select-modal-label'
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
    >
      {children}
    </div>
  )
}

function SelectModalItem({
  className,
  children,
  customItem: CustomItem,
  value: itemValue,
  disabled,
}: SelectModalItemProps) {
  const { selectedValue, onValueSelect } = useSelectModalContext()
  const isSelected = selectedValue === itemValue

  const handleClick = () => {
    if (!disabled) {
      onValueSelect(itemValue)
    }
  }

  if (CustomItem) {
    return (
      <div
        data-slot='select-modal-item'
        onClick={handleClick}
        className={cn(
          'outline-hidden cursor-pointer',
          disabled && 'opacity-50 pointer-events-none',
          className,
        )}
      >
        <CustomItem value={itemValue} selected={isSelected} disabled={disabled}>
          {children}
        </CustomItem>
      </div>
    )
  }

  return (
    <div
      data-slot='select-modal-item'
      onClick={handleClick}
      className={cn(
        "focus:bg-neutral-surface-20 focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 hover:bg-neutral-surface-10",
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      {isSelected && (
        <span className='absolute right-2 flex size-3.5 items-center justify-center'>
          <CheckIcon className='size-4' />
        </span>
      )}
      {children}
    </div>
  )
}

function SelectModalSeparator({ className }: SelectModalSeparatorProps) {
  return (
    <div
      data-slot='select-modal-separator'
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
    />
  )
}

function SelectModalItems<T = unknown>({
  items,
  getValue,
  renderItem,
  customItem,
  getDisabled,
  className,
  filterFn,
}: SelectModalItemsProps<T>) {
  const { searchTerm } = useSelectModalContext()

  const filteredItems = React.useMemo(() => {
    if (!searchTerm) return items

    if (filterFn) {
      return items.filter((item) => filterFn(item, searchTerm))
    }

    // Default filter: search in value
    const lowerSearchTerm = searchTerm.toLowerCase()
    return items.filter((item, index) => {
      const value = getValue(item, index)
      return value.toLowerCase().includes(lowerSearchTerm)
    })
  }, [items, searchTerm, filterFn, getValue])

  return (
    <>
      {filteredItems.map((item) => {
        const originalIndex = items.indexOf(item)
        return (
          <SelectModalItem
            key={getValue(item, originalIndex)}
            value={getValue(item, originalIndex)}
            customItem={customItem}
            disabled={getDisabled?.(item, originalIndex)}
            className={className}
          >
            {renderItem(item, originalIndex)}
          </SelectModalItem>
        )
      })}
    </>
  )
}

export {
  SelectModal,
  SelectModalContent,
  SelectModalGroup,
  SelectModalItem,
  SelectModalItems,
  SelectModalLabel,
  SelectModalSeparator,
  SelectModalTrigger,
  SelectModalValue,
}
